<template>
  <section class="ThePrivacyPolicy">
    <h1 class="ThePrivacyPolicy_Heading">Grove Collaborative Privacy Policy</h1>
    <p class="ThePrivacyPolicy_EmphasizedEffectiveDate">
      Effective Date: July 12, 2023
    </p>

    <PoliciesGrovePronounDisclaimer />

    <p>
      This Privacy Policy describes the personally identifiable information from
      or about an individual (<strong>"Personal Information"</strong>) that we
      gather from you, how we use and disclose that information, and the ways in
      which you can control how we use and share it. By using our Service or
      when applying for or accepting a position with us, you acknowledge that
      you have read, understood and agreed to the terms of this Privacy Policy.
      If you are unable to review or access this Privacy Policy and require
      assistance, please contact us at:
      <SkyLink to="mailto:support@grove.co" inline tag="a">
        support@grove.co</SkyLink
      >.
    </p>

    <p>
      When you use the Service, this Privacy Policy is incorporated into and is
      subject to the Grove Collaborative
      <SkyLink inline to="/terms">Terms of Use</SkyLink>. Please make sure that
      you have carefully read and understand the Grove Collaborative
      <SkyLink inline to="/terms">Terms of Use</SkyLink> before you use our
      Service. Capitalized terms that are not defined in the privacy policy have
      the meaning given to them in the Grove Collaborative
      <SkyLink inline to="/terms">Terms of Use</SkyLink>.
    </p>

    <p>
      Residents of California, Colorado, Connecticut, Nevada, Utah, and Virginia
      may have additional rights. Please see our
      <SkyLink to="#supplemental-state-privacy">
        Supplemental Privacy Notices
      </SkyLink>
      for each of these states.
    </p>

    <h3 id="info-we-collect" class="ThePrivacyPolicy_Subheading">
      TYPE OF INFORMATION WE COLLECT
    </h3>

    <p>
      How Information is Collected. Grove Collaborative collects your
      information in two ways:
    </p>
    <ul class="ThePrivacyPolicy_BulletPoints">
      <li>
        Directly (for example, when you provide information when you use or
        visit the Service or submit a job application).
      </li>
      <li>
        Indirectly (for example, through technology used within the Service or
        within our talent management tools).
      </li>
    </ul>
    <h4 class="ThePrivacyPolicy_SectionHeading">
      Information That You Give Us Directly
    </h4>

    <p>
      We receive and store any information you voluntarily give to us when you
      use the Service, submit a job application or work for us. You may choose
      not to provide certain information, but then you might not be able to use
      the Service, take advantage of certain Service features, or complete job
      related activity with us. You provide most of this information when you
      interact with the Service or our talent management tools. Some examples of
      of this type of information are below.
    </p>

    <ul class="ThePrivacyPolicy_BulletPoints">
      <li>
        When you create an account or login to your account, the information
        that we request such as your first and last name, email address,
        shipping address, billing address, phone number, and password.
      </li>

      <li>
        When you order products on our Service, the information necessary to
        complete the transaction, such as your name, items purchased, special
        instructions, date and time of order, purchase price information,
        limited credit card information (last four digits and expiration date),
        billing information and shipping information.
      </li>

      <li>
        When you interact with certain Service features, the information that
        you provide during that interaction, such as (i) product reviews, (ii)
        information required to participate in one of our sweepstakes,
        giveaways, contests, customer panels, or other programs, (iii)
        information that you submit or provide if you choose to participate in
        one of our surveys, quizzes, or other questionnaries - including the
        Grove Wellness quiz, and (iv)information required to verify your
        identity. Please do not post or add Personal Information in your
        reviews.
      </li>

      <li>
        When you contact us, the information that you provide and the
        information that we need to respond, such as your name, email address,
        your message to us, and our response.
      </li>

      <li>
        When you visit us at our offices or locations, information that you
        provide, such as your name and email address and information gathered
        from security surveillance.
      </li>

      <li>
        When you interact with us as part of a business relationship,
        professional information that you provide such as your job title and
        information about your employer.
      </li>
    </ul>

    <h4 class="ThePrivacyPolicy_SectionHeading">
      Information That You Give Us via the Grove Careers Page
    </h4>
    <p>
      You can search for open positions and apply for a job with Grove
      Collaborative through our Careers site.
    </p>
    <ul class="ThePrivacyPolicy_BulletPoints">
      <li>
        When you submit a job application with us, you provide requested
        information, such as your name, email address, postal address, telephone
        number, military or veteran status, job history, and educational
        information.
      </li>
      <li>
        If you send us an email regarding a position, the information we receive
        is the information you include in your cover letter, resume and email
        message.
      </li>
      <li>
        By providing us with your information in connection with employment
        opportunities, you authorize us to transmit and store your information
        in our recruitment database, to circulate that information as necessary
        for the purpose of evaluating your qualifications for job openings with
        Grove Collaborative, and to contact you about possible employment
        opportunities. If you are hired by Grove Collaborative, the information
        you provide during the recruiting process will become part of our
        employee records.
      </li>
    </ul>

    <h4 class="ThePrivacyPolicy_SectionHeading">
      Information Collected Automatically
    </h4>

    <p>
      When you use the Service, we collect information automatically to help us
      operate and improve the Service or process a job or business related
      interaction, such as the Internet protocol (IP) address used to connect
      your computer to the Internet; device data, login; email address;
      password; computer and connection information such as browser type,
      version, and time zone setting, browser plug-in types and versions,
      operating system, and platform; the full Uniform Resource Locator (URL);
      information regarding your interaction with our Service and
      advertisements; inferences such as recommendations of products based on
      your purchase or browsing history; clickstream to, through, and from any
      of our Internet web sites, including date and time; cookie number;
      products you viewed or searched for; and the phone number you used to call
      our 800 number. We may also use browser data such as cookies or similar
      technologies on certain parts of our website for business processes, fraud
      prevention and other purposes. For details about how we use these
      technologies, and your opt-out opportunities and other options, please see
      our
      <SkyLink inline to="/policies#cookie-policy">Cookie Policy</SkyLink>. We
      receive information from you and third-parties that helps us approximate
      your location. During some visits we may use software tools such as
      JavaScript to measure and collect session information, including page
      response times, download errors, length of visits to certain pages, page
      interaction information (such as scrolling, clicks, and mouse-overs), and
      methods used to browse away from the page. We may also collect technical
      information to help us identify your device for fraud prevention and
      diagnostic purposes.
    </p>

    <h4 class="ThePrivacyPolicy_SectionHeading">
      Information from Other Sources
    </h4>

    <p>
      We may receive or collect information about you from third parties and
      combine and store it on our servers with other information we may have
      already received or collected from you. We may store this information or
      combine it with information we collect in other ways. Some examples
      include:
    </p>

    <ul class="ThePrivacyPolicy_BulletPoints">
      <li>
        Service partners that provide or make available advertising, features
        and functionality, and content on or through the Service.
      </li>

      <li>
        Partners that collect and maintain information on their consumers. These
        partners may provide Grove Collaborative with certain information about
        those consumers for the purposes of serving advertisements and/or
        marketing offers to their consumers.
      </li>

      <li>
        Marketing companies and data providers that create, maintain, and
        distribute professional marketing lists or segments, or maintain and
        distribute other marketing, or similar information.
      </li>

      <li>
        Governmental or quasi-governmental agencies or organizations that
        provide or make available, to the public, census and demographic data.
      </li>

      <li>
        Third-party social media websites, applications, or services that you
        use, when we allow those websites, applications, or services to interact
        through the Service to provide personalized services to you. In some
        cases, those websites, applications, or services may automatically
        provide us with information about you to facilitate personalization
        unless you use the controls available on those websites, applications,
        or services to opt-out of such sharing.
      </li>
    </ul>

    <h3 id="how-info-used" class="ThePrivacyPolicy_Subheading">
      HOW WE USE THE INFORMATION THAT WE COLLECT
    </h3>

    <p>
      We use your Personal Information in ways that help us operate and improve
      the Service and complete requested business activities. Some examples
      include:
    </p>

    <ul class="ThePrivacyPolicy_BulletPoints">
      <li>
        To fulfill your requests or to meet the reason you provided the
        information. For example, if you share your name and contact information
        to ask a question about our products or Service, we will use that
        Personal Information to respond to your inquiry. If you provide your
        Personal Information to purchase a product or service, we will use that
        information to process your payment and facilitate delivery. We may also
        save your information to facilitate new product orders or process
        returns. If you choose to provide responses to the Grove Wellness quiz
        to help you identify which products might meet you needs, we will use
        this information such as your email, nutritional needs, health concerns,
        dietary preferences, and other health-related information to display,
        email and offer product information.
      </li>

      <li>
        To personalize and provide the Service, such as to identify you as a
        consumer, to establish and maintain your account, to remember your
        information so that you do not have to re-enter it, to provide the
        transactions that you request, to assess the current and future needs of
        your household, to process and fulfill your orders, provide refunds.
      </li>

      <li>
        To contact you for administrative purposes, legal purposes, and certain
        Service-related purposes, such as to send you emails, text messages, or
        other communications about your orders, consumer support requests, and
        products and services that we believe may interest you.
      </li>

      <li>
        For marketing and advertising purposes, such as displaying content based
        upon your interests or providing promotional and advertising materials
        (either directly or through our service providers) about our products
        and Services, including relevant product information based on your
        responses to our Wellness Quiz. You may opt-out of our marketing
        communications by submitting a request by clicking
        <SkyLink to="https://preferences.grove.co/dont_sell" inline tag="a">
          here</SkyLink
        >.
      </li>

      <li>
        To contract or partner with third parties to provide specialized
        services on our behalf, such as development, hosting, analytics,
        maintenance, payment processing, products, shipping, message delivery,
        payment processors, consumer support, ad servers or other providers of
        advertising services, platform technology providers, bulk email
        processors, parties who assist us with sweepstakes management and prize
        fulfillment, and other services relating to the operation of the
        Service.
      </li>

      <li>To test, research, and analyze our Services.</li>

      <li>
        To help maintain the safety, security, and integrity of our Service,
        databases and other technology assets, and business.
      </li>

      <li>To evaluate any job applications you may submit to us.</li>

      <li>
        To respond to law enforcement requests and as required by applicable
        law, court order, or governmental regulations.
      </li>

      <li>
        To execute any other purpose disclosed to you at the time we collect or
        receive the information, or otherwise with your consent.
      </li>
    </ul>

    <h3 id="disclosure-policy" class="ThePrivacyPolicy_Subheading">
      WHEN WE DISCLOSE THE INFORMATION THAT WE COLLECT
    </h3>

    <p>
      We will share categories of your Personal Information for business
      purposes with certain third parties that help us perform our business
      operations or that you designate to receive such information, including
      other websites, social media platforms, other individuals, and other
      businesses. For example, we may disclose your Personal Information to:
    </p>

    <ul class="ThePrivacyPolicy_BulletPoints">
      <li>
        Service providers who provide specialized services on our behalf such as
        development, hosting, analytics, maintenance, payment processing,
        products, shipping, message delivery, text messaging, consumer support,
        ad servers or other providers of advertising services, platform
        technology providers, bulk email processors, talent management tools,
        benefits providers, parties who assist us with sweepstakes management
        and prize fulfillment, and other services relating to the operation of
        our business.
      </li>

      <li>
        Third party payment processors who process your payment when you make a
        purchase on the Service. We rely on these third parties (such as Paypal
        and Stripe) to collect and process directly payment and financial
        information you provide to pay for your purchases. The terms and
        policies regarding the security practices of and use of your information
        by the payment processors is governed by the terms and conditions of
        these third parties, and we encourage you to read their terms carefully
        before submitting your information.
      </li>

      <li>
        Other parties that we think are necessary to: (i) take precautions
        against liability; (ii) protect Grove Collaborative and others from
        fraudulent, abusive, or unlawful uses or activity; (iii) investigate and
        defend ourselves against any third party claims or allegations; (iv)
        protect the security or integrity of the Service; and/or (v) protect the
        rights, property, or personal safety of Grove Collaborative, our users,
        or others. We may also disclose your information if required to do so by
        law or if we believe that such action is necessary to comply with state
        and federal laws; in response to a court order, judicial or other
        government subpoena or warrant; or to otherwise cooperate with law
        enforcement activity.
      </li>
    </ul>

    <p>
      If we engage in a joint promotion with a business partner, we may share
      your contact information with our business partner to be used in relation
      to the promotion. We may share aggregated or non-personal information
      (such as the number of daily visitors to a particular web page or the size
      of an order placed on a certain date) with third parties such as
      advertising partners. We can also share your Personal Information as part
      of a sale, merger or change in control, or in preparation for any of these
      events. Any other entity which buys Grove Collaborative or part of our
      business will have the right to continue to use your information, but only
      in the manner set out in this Privacy Policy unless you agree otherwise.
    </p>

    <h4 class="ThePrivacyPolicy_SectionHeading">
      Use of Anonymized Information
    </h4>

    <p>
      We anonymize and aggregate certain Personal Information we collect (so
      that it does not directly identify you). We may use anonymized information
      for purposes that include testing, research, data analysis, and Service
      improvements. We may also share such anonymized information with others.
    </p>

    <h4 class="ThePrivacyPolicy_SectionHeading">
      Information You Provide About Others
    </h4>

    <p>
      If you provide Personal Information about another person
      (<strong>“Others"</strong>) in connection with using the Service (such as
      programs through which refer a friend or family member) or interacting
      with Grove Collaborative in a business relationship (such as on job
      applications or to complete employment benefit information), we rely upon
      you to obtain any consents from Others that may be required by law to
      allow us to use the Personal Information of such Others. We will use the
      Personal Information of such Others as set forth in this Privacy Policy.
    </p>

    <h3 id="data-retention" class="ThePrivacyPolicy_Subheading">
      Data Retention
    </h3>

    <p>
      We retain information from or about you for so long as necessary to
      fulfill the purposes outlined in this Privacy Policy. When the information
      is no longer necessary for these purposes, we may delete it or keep it in
      a form that does not identify you, unless we are required by law to keep
      this information for a longer period. When determining the retention
      period, we take into account various criteria, such as the type of
      products and services requested by or provided to you, the nature and
      length of our relationship with you, possible re-enrolment with our
      products or services, the impact on the services we provide to you if we
      delete some information from or about you, mandatory retention periods
      provided by law and the statute of limitations. You may have a right to
      request deletion of your Personal Information if you are a resident of
      certain states, and we will use commercially reasonable efforts to honor
      your request where required by law. However, we may be required to keep
      certain Personal Information and not delete it or to retain Personal
      Information for a certain time. In these cases, we will comply with your
      deletion request only after we have fulfilled applicable requirements.
      Deleted Personal Information is removed from Grove Collaborative’s active
      database, but may remain in our archives. We may also retain your Personal
      Information for fraud prevention or similar purposes.
    </p>

    <h3 id="third-party-services" class="ThePrivacyPolicy_Subheading">
      Links to Third Party Services
    </h3>

    <p>
      The Service may also contain features or links to websites and services
      provided by third parties that we do not operate and are outside of our
      control. Any Personal Information you provide on these third party sites
      or services is provided directly to that third party and is subject to
      that third party's policies, if any, governing privacy and security, even
      if accessed through the Service. Please exercise caution and review their
      applicable privacy and security policies before providing them with
      Personal Information. We are also not responsible for any products or
      services you may purchase from these third-party sites. We do not and will
      not provide any representations or recommendations in relation to any of
      the information and suggestions comprised within the services. We do not
      guarantee or endorse the products or services offered by the third party.
      You are deemed to be responsible for and shall use your own skill and
      judgment as to, the quality, value and suitability of such information and
      suggestions and in deciding whether to enter into any contract with any
      third party for the supply of services or sale of goods.
    </p>

    <h3 class="ThePrivacyPolicy_Subheading">
      Our Commitment To Children's Privacy
    </h3>

    <p>
      We do not knowingly allow children under 16 years of age to use the
      Service or knowingly collect or maintain Personal Information from persons
      under 13 years of age. If you are under 16 years of age, then please do
      not use or access the Service at any time or in any manner. If a child
      under 13 submits Personal Information to Grove Collaborative and we learn
      that the Personal Information is the information of a child under 16, we
      will attempt to delete the information as soon as possible. If you believe
      that we might have any Personal Information from a child under 16, please
      contact us at
      <SkyLink inline to="mailto:legal@grove.co">legal@grove.co</SkyLink>.
    </p>

    <h3 class="ThePrivacyPolicy_Subheading">Our Commitment To Data Security</h3>
    <p>
      We use certain physical, managerial, and technical controls that are
      designed to improve the integrity and security of your Personal
      Information. We cannot, however, ensure or warrant the security of any
      information you transmit to us over the internet or store on the Service
      and you do so at your own risk. We also cannot guarantee that such
      information will not be accessed, disclosed, altered, or destroyed by
      breach of any of our physical, technical, or managerial controls.
    </p>
    <p>
      If we learn of a security systems breach, then we may attempt to notify
      you electronically so that you can take appropriate protective steps. We
      may post a notice through the Service if a security breach occurs.
      Depending on where you live, you may have a legal right to receive notice
      of a security breach in writing. To receive a free written notice of any
      security breaches, you should notify us at
      <SkyLink inline to="mailto:support@grove.co">support@grove.co</SkyLink>.
    </p>

    <h3 class="ThePrivacyPolicy_Subheading">International visitors</h3>
    <p>
      The Service is hosted in the United States and is intended solely for
      consumers located within the United States. If you choose to use the
      Service from the European Union or other regions of the world with laws
      governing data collection and use that may differ from U.S. law, then
      please note that you are transferring your personally identifiable
      information outside of those regions to the United States for storage and
      processing, and by providing your personally identifiable information on
      the Service you consent to that transfer, storage, and processing. Please
      note that the United States does not have the same data protection laws as
      the European Union and other regions.
    </p>

    <h3 class="ThePrivacyPolicy_Subheading">
      Changes and updates to this Privacy Policy
    </h3>
    <p>
      Please revisit this page periodically to stay aware of any changes to this
      Privacy Policy. If we modify this Privacy Policy, we will make the
      modified version available through the Service and update the last updated
      data above. In the event that the modifications materially alter your
      rights or obligations hereunder, we will make reasonable efforts to notify
      you of the change. Our amended Privacy Policy will automatically take
      effect 30 days after it is made available through the Service, and if you
      do not agree with any changes to the Privacy Policy, you may terminate
      your account and stop using the Service. Your continued use of the Service
      after the revised Privacy Policy has become effective indicates that you
      have read, understood and agreed to the then-current version of the
      Privacy Policy.
    </p>

    <h4 class="ThePrivacyPolicy_SectionHeading">Our contact information</h4>
    <p>
      Please contact us with any questions or comments about this Privacy
      Policy, your Personal Information, our use and disclosure practices, or
      your consent choices by e-mail at
      <SkyLink inline to="mailto:dataprivacy@grove.co"
        >dataprivacy@grove.co</SkyLink
      >. You may also contact us at the following address:
    </p>
    <div class="ThePrivacyPolicy_AddressLine">Grove Collaborative, Inc.</div>
    <div class="ThePrivacyPolicy_AddressLine">1301 Sansome Street</div>
    <div class="ThePrivacyPolicy_AddressLine">San Francisco, CA 94111</div>

    <h2 id="supplemental-state-privacy" class="ThePrivacyPolicy_Center">
      SUPPLEMENTAL STATE SPECIFIC PRIVACY NOTICE
    </h2>

    <p>
      This Supplemental State Specific Privacy Notice (<strong
        >“Supplemental State Notice”</strong
      >) applies only to Personal Information collected about consumers residing
      in California, Colorado, Connecticut, Nevada, Utah, and Virginia and
      supplements the information contained in the Privacy Policy. It provides
      information required under the California Consumer Privacy Act of 2018, as
      amended by the California Privacy Rights Act of 2020 (collectively, the
      <strong>“CCPA”</strong>), the Colorado Privacy Act of 2021 (the
      <strong>“CPA”</strong>), the Connecticut Data Privacy Act of 2022
      (<strong>“CDPA”</strong>) the Nevada Privacy Act (the
      <strong>“NPA”</strong>), the Utah Consumer Privacy Act of 2022 (the
      <strong>“UCPA”</strong>) and the Virginia Consumer Data Protection Act of
      2021 (the <strong>“VCDPA”</strong>) (all of which are collectively
      referred to as the <strong>“State Privacy Laws”</strong>) and their
      related regulations.
    </p>

    <p>
      This Supplemental State Notice describes our practices regarding the
      collection, use, and disclosure of Personal Information and instructions
      for submitting data subject requests for consumers residing in states
      covered by the State Privacy Laws. If you are unable to review or access
      this Supplemental State Notice and require assistance, please contact us
      at:
      <SkyLink to="mailto:support@grove.co" inline tag="a">
        support@grove.co</SkyLink
      >.
    </p>

    <h3 class="ThePrivacyPolicy_Subheading">Definitions Used in this Policy</h3>

    <p>
      <strong>“consumer”</strong> means a natural person who resides in
      California, Colorado, Connecticut, Nevada, Utah and Virginia and to whom
      we offer the Services. For purposes of this Supplemental Policy, this term
      includes natural persons who reside in California and engage with us as
      part of business-to-business transactions, submit a job application to
      Grove Collaborative or work for Grove Collaborative.
    </p>

    <p>
      <strong>“Personal Information”</strong> means information that identifies,
      relates to, describes, is reasonably capable of being associated with, or
      could reasonably be linked, directly or indirectly, with a particular
      consumer or household. Personal Information includes “personal data” as
      that term is defined in the CPA, VCDPA, UCPA, NPA and CDPA. Personal
      Information also includes “Sensitive Personal Information,” as defined
      below.
    </p>

    <p>
      <strong>“Sensitive Personal Information”</strong> means personal
      information that reveals a consumer’s: 1) social security, driver’s
      license, state identification card, or passport number; 2) account log-in,
      financial account number, debit card number, or credit card number in
      combination with any required security or access code, password, or
      credentials allowing access to the individual’s account; 3) precise
      geolocation; 4) racial or ethnic origin; 5) religious beliefs; 5) union
      membership; 6) contents of email or text messages, unless we are the
      intended recipient; 7) genetic data; 8) biometric information used to
      uniquely identify the consumer, and 9) health information, sex life, or
      sexual orientation. Sensitive Personal Information also includes
      “sensitive data” as that term is defined in the CCPA, CPA, CDPA, UCPA, and
      VCDPA.
    </p>

    <p>
      <strong>“third party” </strong>means a person or organization which is not
      a consumer, service provider, or an entity owned or controlled by us and
      as defined by the CCPA, CPA, CDPA, UCPA, and VCDPA.
    </p>

    <p>
      <strong>“service provider”</strong> means a “vendor,” “contractor,” or
      “processor” which collects, stores, or otherwise handles data for us, as
      those terms are defined in the CCPA, CPA, CDPA, NPA, UCPA, and VCDPA.
    </p>

    <p>
      Other terms used in this Supplemental State Notice are as defined under
      the CPA, CPA, CDPA, NPA, UCPA, and/or VCDPA. If a definition varies under
      the different laws, the definition that applies in your state will apply.
      For example, if you are a California consumer, terms used in this
      Supplemental State Notice will have the definition used in the CCPA.
    </p>

    <h3 class="ThePrivacyPolicy_Subheading">
      The Personal Information We Collect and Disclose
    </h3>

    <p>
      Below is a summary of the Personal Information we may collect (including
      the information collected from consumers over the past 12 months), the
      reason we collect the Personal Information, the types of sources from
      which each category of Personal Information is collected, and the type of
      third parties with whom we may share consumer Personal Information. As the
      summary shows, we may share or sell Personal Information to third parties
      or disclose certain Personal Information to service providers for business
      purposes.The section references relate to the sections above in the
      Privacy Policy.
    </p>

    <PoliciesPIITable />

    <h3 class="ThePrivacyPolicy_Subheading">
      Your Rights Related to Your Personal Information
    </h3>

    <p>
      As required by the State Privacy Laws, we provide detailed information
      below regarding the data subject rights available to California, Colorado,
      Utah, Nevada, Connecticut and Virginia consumers. Generally, the rights
      which you have can be categorized as follows:
    </p>

    <ul class="ThePrivacyPolicy_BulletPoints">
      <li>Know whether we process your Personal Information.</li>
      <li>Access and delete certain Personal Information.</li>
      <li>Data portability.</li>
      <li>
        Opt-out of Personal Information processing for targeted advertising and
        sales
      </li>
      <li>
        Correct inaccuracies in your Personal Information, taking into account
        the information’s nature processing purpose.
      </li>
      <li>
        Opt-out of profiling in furtherance of decisions that produce legal or
        similarly significant effects.
      </li>
    </ul>

    <p>
      <strong>Processing of Sensitive Information:</strong> We must obtain your
      express consent prior to collecting and processing certain categories of
      Sensitive Personal Information such as health data, data about protected
      characteristics and genetic or biometric data.
    </p>

    <p>
      <strong>Non-Discrimination:</strong> Grove Collaborative will not
      discriminate against customers who exercise their rights under the State
      Privacy Laws.
    </p>

    <h4 class="ThePrivacyPolicy_SectionHeading">
      Personal Information of Minors
    </h4>
    <p>
      The Service is not intended for children or minors under the age of 16.
      Accordingly, we do not knowingly store information from minors under the
      age of 16 except as required pursuant to applicable law. If you believe
      that a child has submitted Personal Information to us, please contact us
      at dataprivacy@grove.co, and we will delete the information.
    </p>

    <h4 class="ThePrivacyPolicy_SectionHeading">
      Financial Incentives for Consumers
    </h4>
    <p>
      Grove Collaborative does not have programs that provide financial
      incentive to consumers who allow us to retain and share their Personal
      Information.
    </p>
  </section>
</template>

<style lang="scss">
.ThePrivacyPolicy {
  display: grid;
  row-gap: var(--spacing-3x);

  &_Heading {
    @include type-brand-heading('1');
    margin-top: var(--spacing-8x);
  }

  &_Subheading {
    @include type-brand-heading('3');
    margin-top: var(--spacing-6x);
  }

  &_SectionHeading {
    @include type-brand-heading('4');
    margin-top: var(--spacing-6x);
  }

  &_EmphasizedEffectiveDate {
    @include type-body('l', true);
    margin-bottom: var(--spacing-6x);
  }

  &_BulletPoints {
    margin: 0;
    display: grid;
    row-gap: var(--spacing-2x);
  }

  &_AddressLine {
    display: block;
    margin-bottom: var(--spacing-1x);
  }

  &_ContactInformation {
    margin-bottom: var(--spacing-10x);
  }

  &_Center {
    @include type-brand-heading('2');
    margin-top: var(--spacing-8x);
    text-align: center;
  }
}
</style>
